import React, { useState, useEffect } from 'react';
import { IoInformationCircleOutline, IoCheckmarkOutline, IoPlayCircleOutline, IoBookOutline, IoCopyOutline } from 'react-icons/io5';
import { HiChevronRight } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

export default function DepositBank() {
    const { t } = useTranslation();
    const [selectedBank, setSelectedBank] = useState('');
    const [selectedAccount, setSelectedAccount] = useState('');
    const [riskAccepted, setRiskAccepted] = useState(false);
    const [copiedStates, setCopiedStates] = useState({
        accountHolder: false,
        iban: false
    });
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [banks, setBanks] = useState([]);
    const [banksLoading, setBanksLoading] = useState(true);

    const getBankIcon = (bankName) => {
        const name = bankName?.toLowerCase() || '';
        if (name.includes('ziraat')) return '/assets/images/banks/ziraat.png';
        if (name.includes('iş') || name.includes('is ')) return '/assets/images/banks/isbank.png';
        if (name.includes('garanti')) return '/assets/images/banks/garanti.png';
        if (name.includes('akbank')) return '/assets/images/banks/akbank.svg';
        if (name.includes('yapı') || name.includes('yapi')) return '/assets/images/banks/yapikredi.png';
        return null;
    };

    const selectedBankData = banks.find(bank => bank.name === selectedBank);

    // Get bank payment methods
    useEffect(() => {
        const fetchBankMethods = async () => {
            try {
                setBanksLoading(true);
                const response = await api.getBankPaymentMethods();
                const rawData = response.data?.data || response.data;
                if (rawData && rawData.length > 0) {
                    const mappedBanks = rawData.map(bank => ({
                        name: bank.bankName,
                        code: bank.bankName,
                        icon: getBankIcon(bank.bankName),
                        accounts: Object.entries(bank.ibans || {})
                            .filter(([, iban]) => iban && iban !== '-')
                            .map(([currency, iban]) => ({
                                type: currency,
                                iban: iban,
                                accountHolder: bank.beneficiary
                            }))
                    }));
                    setBanks(mappedBanks);
                    setSelectedBank(mappedBanks[0].name);
                    setSelectedAccount(mappedBanks[0].accounts[0]?.type || '');
                }
            } catch (error) {
                console.error('Error fetching bank payment methods:', error);
            } finally {
                setBanksLoading(false);
            }
        };
        fetchBankMethods();
    }, []);

    // Get user info on component mount
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                setLoading(true);
                const response = await api.getUserInfo();
                const userData = response.data?.data || response.data;
                // Map camelCase to PascalCase for consistency
                const normalizedData = {
                    ...userData,
                    Balance: userData.Balance ?? userData.balance,
                    Credit: userData.Credit ?? userData.credit,
                    FullName: userData.FullName ?? userData.fullName
                };
                setUserInfo(normalizedData);
            } catch (error) {
                console.error('Error fetching user info:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserInfo();
    }, []);

    const handleBankChange = (bankName) => {
        setSelectedBank(bankName);
        const newBankData = banks.find(bank => bank.name === bankName);
        if (newBankData && newBankData.accounts.length > 0) {
            setSelectedAccount(newBankData.accounts[0].type);
        }
    };

    const handleAccountChange = (accountType) => {
        setSelectedAccount(accountType);
    };

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            const successMessage = type === 'accountHolder' ? t('depositBank.toast.accountHolderCopied') : t('depositBank.toast.ibanCopied');
            toast.success(successMessage);
            
            // Tik işareti göster
            setCopiedStates(prev => ({
                ...prev,
                [type]: true
            }));
            
            // 3 saniye sonra eski haline döndür
            setTimeout(() => {
                setCopiedStates(prev => ({
                    ...prev,
                    [type]: false
                }));
            }, 3000);
        } catch (err) {
            console.error('Kopyalama hatası:', err);
            toast.error(t('depositBank.toast.copyFailed'));
        }
    };

    return (
        <div className='deposit-layout'>
            <div className="container py-lg-5">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="deposit-page">
                            {/* Header */}
                            <div className="deposit-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-3">
                                        <h1 className="deposit-title">{t('depositBank.title')}</h1>
                                    </div>
                                    <Link to="/deposit" className="p2p-link d-flex align-items-center gap-2">
                                        <span>{t('depositBank.cryptoDepositLink')}</span>
                                        <HiChevronRight size={16} />
                                    </Link>
                                </div>
                            </div>

                            <div className="row g-4">
                                {/* Sol Kolon - Banka İşlemleri */}
                                <div className="container">
                                    {/* Yatay Varlıklar Kartı */}
                                    <div className="assets-card-horizontal">
                                        <div className="assets-horizontal-grid">
                                            <div className="asset-balance-item left">
                                                <div className="asset-balance-label">
                                                    <span className="asset-dot available"></span>
                                                    {t('deposit.assets.title')}
                                                </div>
                                                <div className="asset-balance-value">
                                                    {loading ? '...' : `${((userInfo?.Balance || 0) + (userInfo?.Credit || 0)).toFixed(2)} USD`}
                                                </div>
                                            </div>

                                            <div className="asset-total-center">
                                                <div className="asset-total-label">{t('deposit.assets.availableBalance')}</div>
                                                <div className="asset-total-amount">
                                                    <span>{loading ? '...' : `${(userInfo?.Balance || 0).toFixed(2)} USD`}</span>
                                                </div>
                                            </div>

                                            <div className="asset-balance-item right">
                                                <div className="asset-balance-label">
                                                    <span className="asset-dot frozen"></span>
                                                    {t('deposit.assets.availableCredit')}
                                                </div>
                                                <div className="asset-balance-value">
                                                    <span>{loading ? '...' : `${(userInfo?.Credit || 0).toFixed(2)} USD`}</span>
                                                </div>  
                                            </div>
                                        </div>
                                    </div>

                                    <div className="deposit-actions">
                                        {/* 1. Banka Seçimi */}
                                        <div className="deposit-step">
                                            <div className="step-number">1</div>
                                            <div className="step-content">
                                                <h3 className="step-title">{t('depositBank.steps.selectBank')}</h3>
                                                <div className="coin-selector mt-lg-3">
                                                    <div className="coin-options">
                                                        {banksLoading ? (
                                                            <div className="text-center py-3">
                                                                <div className="spinner-border text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            banks.map((bank) => (
                                                                <button
                                                                    key={bank.name}
                                                                    className={`coin-option ${selectedBank === bank.name ? 'active' : ''}`}
                                                                    onClick={() => handleBankChange(bank.name)}
                                                                >
                                                                    {bank.icon && <img className='rounded-0' src={bank.icon} alt={bank.name} width="82" />}
                                                                    <div className="coin-option-info">
                                                                        <span className="coin-option-symbol">{bank.code}</span>
                                                                        <span className="coin-option-name">{bank.name}</span>
                                                                    </div>
                                                                </button>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2. Hesap Türü ve Banka Bilgileri */}
                                        <div className="deposit-step">
                                            <div className="step-number">2</div>
                                            <div className="step-content">
                                                <h3 className="step-title">{t('depositBank.steps.accountTypeAndInfo')}</h3>

                                                {/* Seçilen Banka Bilgisi */}
                                                <div className="selected-coin-info mt-lg-3">
                                                    <div className="coin-display">
                                                        {selectedBankData?.icon && <img className='rounded-0' src={selectedBankData?.icon} alt={selectedBankData?.name} width="75" />}
                                                        <div className="coin-details">
                                                            <span className="coin-symbol">{selectedBankData?.code}</span>
                                                            <span className="coin-name">{selectedBankData?.name}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Hesap Türü Seçimi */}
                                                <div className="network-selector">
                                                    <div className="network-label">
                                                        <span>{t('depositBank.accountSelection.selectAccountType')}</span>
                                                        <IoInformationCircleOutline size={16} className="info-icon" />
                                                    </div>
                                                    <div className="network-options">
                                                        {selectedBankData?.accounts.map((account) => (
                                                            <button
                                                                key={account.type}
                                                                className={`network-option ${selectedAccount === account.type ? 'active' : ''}`}
                                                                onClick={() => handleAccountChange(account.type)}
                                                            >
                                                                {account.type}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Banka Hesap Bilgileri */}
                                                <div className="wallet-address">
                                                    <div className="section-label mb-lg-3">{t('depositBank.accountSelection.bankAccountInfo')}</div>

                                                    {/* Hesap Sahibi */}
                                                    <div className="bank-info-item">
                                                        <div className="bank-info-label">{t('depositBank.accountSelection.accountHolder')}</div>
                                                        <div className="bank-info-display">
                                                            <span className="bank-info-text">
                                                                {selectedBankData?.accounts.find(a => a.type === selectedAccount)?.accountHolder}
                                                            </span>
                                                            <button
                                                                className={`copy-btn ${copiedStates.accountHolder ? 'copied' : ''}`}
                                                                onClick={() => copyToClipboard(selectedBankData?.accounts.find(a => a.type === selectedAccount)?.accountHolder || '', 'accountHolder')}
                                                            >
                                                                {copiedStates.accountHolder ? <IoCheckmarkOutline size={16} /> : <IoCopyOutline size={16} />}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* IBAN */}
                                                    <div className="bank-info-item">
                                                        <div className="bank-info-label">{t('depositBank.accountSelection.iban')}</div>
                                                        <div className="bank-info-display">
                                                            <span className="bank-info-text">
                                                                {selectedBankData?.accounts.find(a => a.type === selectedAccount)?.iban}
                                                            </span>
                                                            <button
                                                                className={`copy-btn ${copiedStates.iban ? 'copied' : ''}`}
                                                                onClick={() => copyToClipboard(selectedBankData?.accounts.find(a => a.type === selectedAccount)?.iban || '', 'iban')}
                                                            >
                                                                {copiedStates.iban ? <IoCheckmarkOutline size={16} /> : <IoCopyOutline size={16} />}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Risk Uyarısı */}
                                        <div className="risk-warning">
                                            <h4 className="risk-title">{selectedBankData?.name} {t('depositBank.riskWarning.title')}</h4>
                                            <p className="risk-text">
                                                {t('depositBank.riskWarning.description')}
                                            </p>
                                            {/* <div className="risk-checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="risk-accept"
                                                    checked={riskAccepted}
                                                    onChange={(e) => setRiskAccepted(e.target.checked)}
                                                />
                                                <label htmlFor="risk-accept">
                                                    {t('depositBank.riskWarning.acceptTerms')}
                                                </label>
                                            </div> */}
                                        </div>

                                        {/* Onayla Butonu */}
                                        {/* <div className="deposit-actions-footer">
                                            <button
                                                className={`confirm-btn ${riskAccepted ? 'active' : 'disabled'}`}
                                                disabled={!riskAccepted}
                                            >
                                                {t('depositBank.confirmButton')}
                                            </button>
                                        </div> */}
                                    </div>
                                </div>

                                {/* Sağ Kolon - Bilgiler */}
                                {/* <div className="col-lg-4">
                                    <div className="deposit-info"> */}
                                        
                                        {/* <div className="info-section">
                                            <h3 className="info-title">{t('depositBank.assets.title')}</h3>
                                            <div className="assets-summary">
                                                <div className="total-assets">
                                                    {loading ? '...' : `${((userInfo?.Balance || 0) + (userInfo?.Credit || 0)).toFixed(2)} USD`}
                                                </div>
                                                <div className="assets-breakdown">
                                                    <div className="asset-item">
                                                        <div className="asset-dot available"></div>
                                                        <span>{t('depositBank.assets.availableBalance')}:</span>
                                                        <span>{loading ? '...' : `${(userInfo?.Balance || 0).toFixed(2)} USD`}</span>
                                                    </div>
                                                    <div className="asset-item">
                                                        <div className="asset-dot frozen"></div>
                                                        <span>{t('depositBank.assets.availableCredit')}:</span>
                                                        <span>{loading ? '...' : `${(userInfo?.Credit || 0).toFixed(2)} USD`}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Dikkat */}
                                        {/* <div className="info-section">
                                            <h3 className="info-title">{t('depositBank.attention.title')}</h3>
                                            <div className="attention-items">
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('depositBank.attention.minDeposit')}</span>
                                                    <span className="attention-value">100 TL</span>
                                                </div>
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('depositBank.attention.processingTime')}</span>
                                                    <span className="attention-value">1-3 İş Günü</span>
                                                </div>
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('depositBank.attention.commission')}</span>
                                                    <span className="attention-value">Ücretsiz</span>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* SSS */}
                                        {/* <div className="info-section">
                                            <div className="faq-header">
                                                <h3 className="info-title">{t('depositBank.faq.title')}</h3>
                                            </div>
                                            <div className="faq-items">
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <IoInformationCircleOutline size={16} className="faq-icon" />
                                                        <span>{t('depositBank.faq.howToTransfer')}</span>
                                                    </div>
                                                    <button className="self-recovery-btn">{t('depositBank.faq.detailedGuide')}</button>
                                                </div>
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <IoInformationCircleOutline size={16} className="faq-icon" />
                                                        <span>{t('depositBank.faq.transferTime')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="help-links">
                                                <Link to="/bank-guide" className="help-link">
                                                    <IoBookOutline size={16} />
                                                    <span>{t('depositBank.helpLinks.bankGuide')}</span>
                                                </Link>
                                                <Link to="/bank-tutorial" className="help-link">
                                                    <IoPlayCircleOutline size={16} />
                                                    <span>{t('depositBank.helpLinks.videoGuide')}</span>
                                                </Link>
                                            </div>
                                        </div> */}
                                    {/* </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
                <Toaster
                    position="top-center"
                    toastOptions={{
                        duration: 4000,
                        style: {
                            background: '#363636',
                            color: '#fff',
                        },
                        success: {
                            duration: 3000,
                            style: {
                                background: '#10B981',
                                color: '#fff',
                            },
                        },
                        error: {
                            duration: 5000,
                            style: {
                                background: '#EF4444',
                                color: '#fff',
                            },
                        },
                    }}
                />
            </div>
        </div>
    );
}
